/**
 * Common error handling utilities
 * Provides consistent error handling across all tools
 */
class ErrorHandler {
    /**
     * Display a user-friendly error message
     * @param {string} message - Error message to display
     * @param {Error} error - Original error object (optional)
     * @param {string} containerId - Container ID to show error in
     */
    static showError(message, error = null, containerId = 'error-container') {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error('Error container not found:', containerId);
            alert(message);
            return;
        }

        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-message';
        errorDiv.innerHTML = `
            <div class="error-header">
                <span class="error-icon">⚠️</span>
                <span class="error-title">错误</span>
                <button class="error-close" onclick="this.parentElement.parentElement.remove()">×</button>
            </div>
            <div class="error-content">${message}</div>
            ${error ? `<div class="error-detail">${error.message}</div>` : ''}
        `;

        container.appendChild(errorDiv);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            if (errorDiv.parentElement) {
                errorDiv.remove();
            }
        }, 5000);

        // Log to console for debugging
        if (error) {
            console.error('Error:', message, error);
        }
    }

    /**
     * Show a loading indicator
     * @param {string} message - Loading message
     * @param {string} containerId - Container ID
     */
    static showLoading(message = '加载中...', containerId = 'loading-container') {
        const container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = `
            <div class="loading-message">
                <div class="loading-spinner"></div>
                <div class="loading-text">${message}</div>
            </div>
        `;
        container.style.display = 'block';
    }

    /**
     * Hide loading indicator
     * @param {string} containerId - Container ID
     */
    static hideLoading(containerId = 'loading-container') {
        const container = document.getElementById(containerId);
        if (container) {
            container.style.display = 'none';
        }
    }

    /**
     * Show success message
     * @param {string} message - Success message
     * @param {string} containerId - Container ID
     */
    static showSuccess(message, containerId = 'success-container') {
        const container = document.getElementById(containerId);
        if (!container) return;

        const successDiv = document.createElement('div');
        successDiv.className = 'success-message';
        successDiv.innerHTML = `
            <div class="success-header">
                <span class="success-icon">✅</span>
                <span class="success-title">成功</span>
                <button class="success-close" onclick="this.parentElement.parentElement.remove()">×</button>
            </div>
            <div class="success-content">${message}</div>
        `;

        container.appendChild(successDiv);
        
        // Auto-hide after 3 seconds
        setTimeout(() => {
            if (successDiv.parentElement) {
                successDiv.remove();
            }
        }, 3000);
    }

    /**
     * Initialize Chrome extension error suppression
     */
    static initChromeErrorSuppression() {
        // Handle Chrome extension errors
        if (window.chrome && window.chrome.runtime) {
            const originalGetURL = chrome.runtime.getURL;
            chrome.runtime.getURL = function() {
                try {
                    return originalGetURL.apply(this, arguments);
                } catch (e) {
                    console.log('Chrome extension error suppressed');
                    return '';
                }
            };
        }

        // Global error handler
        window.addEventListener('error', function(event) {
            if (event && event.error && event.error.message &&
                (event.error.message.includes('message port closed') ||
                 event.error.message.includes('runtime.lastError'))) {
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        }, true);

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', function(event) {
            if (event && event.reason &&
                (String(event.reason).includes('message port closed') ||
                 String(event.reason).includes('runtime.lastError'))) {
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        });
    }
}

// Initialize error suppression when loaded
document.addEventListener('DOMContentLoaded', () => {
    ErrorHandler.initChromeErrorSuppression();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ErrorHandler;
}
